import { ElementType, forwardRef, MouseEventHandler, ReactElement, ReactNode, Ref } from 'react';
import { Breakpoint, ModalProps, SxProps, Theme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { CheckMarkFill, Exit, Key, QuestionMarkFill, Settings } from '@o2/icons';
import {
  Box,
  Button,
  ButtonProps,
  DisplayNameObj,
  Divider,
  Drawer,
  Link,
  LinkProps,
  Popper,
  Stack,
  Typography,
} from '@o2/ui';
import { clsx } from 'clsx';
import { useUserPopperInternalUtilityClasses } from './userPopperInternalClasses';

export interface UserPopperInternalElmIds {
  root: string;
  verifiedIcon: string;
  verifiedText: string;
  verifyBtn: string;
  userName: string;
  userMobile: string;
  userPhone: string;
  userEmail: string;
  btnSettings: string;
  btnAccesses: string;
  btnLogout: string;
  accountItem: string;
  accountItemRole: string;
  accountsDescription: string;
  accountsLink: string;
}

export interface UserPopperInternalUser {
  /**
   * User ID
   */
  id?: string;

  /**
   *  User name
   */
  name?: ReactNode;

  /**
   * User mobile number
   */
  mobile?: string;

  /**
   * User phone number
   */
  phone?: string;
}

export interface UserPopperInternalAccount {
  /**
   * Account name
   * You can pass Link component if you need.
   */
  name: ReactNode;

  /**
   * Account role
   */
  role: ReactNode;
}

export type UserPopperInternalProps<TLink extends ElementType = 'button'> = {
  /**
   * User popper ID
   */
  id?: string;

  /**
   * Open / close state
   */
  open: boolean;

  /**
   * HTML element to which the UserPopper belongs. Probably a user button on the AppBar.
   */
  targetElm?: Element | null;

  /**
   * Handler for the Popper close event.
   */
  onClose: ModalProps['onClose'];

  /**
   * UserPopperInternalUser object
   */
  user?: UserPopperInternalUser;

  /**
   * Is Customer account verified
   */
  verified?: boolean;

  /**
   * Custom text if customer account verified
   */
  verifiedText?: ReactNode;

  /**
   * Custom text if customer account not verified
   */
  unverifiedText?: ReactNode;

  /**
   * Verified button click handler
   */
  onVerifiedBtnClick?: MouseEventHandler;

  /**
   * Custom verified button text
   */
  btnVerifiedText?: ReactNode;

  /**
   * Logout button click handler
   */
  onLogoutBtnClick?: MouseEventHandler;

  /**
   * Custom logout button text
   */
  btnLogoutText?: ReactNode;

  /**
   * Settings button click handler
   */
  onSettingsBtnClick?: MouseEventHandler;

  /**
   * Custom settings button text
   */
  btnSettingsText?: ReactNode;

  /**
   * Show the Settings button. For the button to actually show up, you also have to pass `onSettingsBtnClick`!
   * @default true
   */
  showSettingsBtn?: boolean;

  /**
   * Access button click handler
   */
  onAccessBtnClick?: MouseEventHandler;

  /**
   * Custom access button text
   */
  btnAccessText?: ReactNode;

  /**
   * Show the Accesses button. For the button to actually show up, you also have to pass `onAccessBtnClick`!
   * @default true
   */
  showAccessBtn?: boolean;

  /**
   * List of UserAccounts (UserPopperInternalAccount)
   */
  accountList?: UserPopperInternalAccount[];

  /**
   * Footer description text
   */
  footerDescription?: ReactNode;

  /**
   * Footer link text
   */
  footerLinkText?: ReactNode;

  /**
   * Footer link click handler
   */
  onFooterLinkClick?: MouseEventHandler;

  /**
   * Breakpoint for switch between Popper and Drawer display
   */
  breakpoint?: Breakpoint;

  /**
   * SX props
   */
  sx?: SxProps;

  /**
   * Element IDs
   */
  elmIds?: Partial<UserPopperInternalElmIds>;

  /**
   * Props passed to the individual subcomponents. Combine with `linkComponent` to avoid TS errors.
   */
  componentProps?: Partial<{
    verifiedBtn: ButtonProps<TLink>;
    settingsBtn: ButtonProps<TLink>;
    accessBtn: ButtonProps<TLink>;
    logoutBtn: ButtonProps<TLink>;
    footerLink: LinkProps<TLink>;
  }>;

  /**
   * What component to render this all links in this component as (inheriting props).
   * Useful when combining with e.g. react-router.
   *
   * The link elements include:
   * - verifiedBtn
   * - settingsBtn
   * - accessBtn
   * - logoutBtn
   * - footerLink
   */
  linkComponent?: TLink;
};

const UserPopperContainer = styled(Box, {
  name: 'O2UserPopperInternal',
  slot: 'container',
})(() => ({
  width: '360px',
  maxWidth: 'calc(100vw - 32px)',
  textAlign: 'center',
}));

const UserPopperContent = styled(Box, {
  name: 'O2UserPopperInternal',
  slot: 'content',
})(({ theme }) => ({
  display: 'block',
  padding: theme.spacing(3, 2),
}));

const UserPopperFooter = styled(Box, {
  name: 'O2UserPopperInternal',
  slot: 'footer',
})(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(0, 2, 3, 2),
  '& > div > table > tbody > tr': {
    height: '32px',
  },
}));

const VerifiedBox = styled(Box, {
  name: 'O2UserPopperInternal',
  slot: 'verifiedBox',
  shouldForwardProp: (propName) => propName !== 'verified',
})<{ verified: boolean }>(({ theme, verified }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(2),
  justifyContent: 'space-between',
  alignItems: 'center',
  minHeight: '48px',
  padding: theme.spacing(1, 2),
  backgroundColor: verified ? theme.palette.background.success : theme.palette.background.warning,
  [`${theme.breakpoints.down('sm')}`]: {
    gap: theme.spacing(1),
    flexDirection: 'column',
  },
  '& > .O2UserPopperInternal-verifiedStatus > svg': {
    color: verified ? theme.palette.success.main : theme.palette.warning.main,
  },
}));

const VerifiedStatus = styled(Box, {
  name: 'O2UserPopperInternal',
  slot: 'verifiedStatus',
})(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(1),
  justifyContent: 'start',
  alignItems: 'start',
}));

const UserName = styled(Box, {
  name: 'O2UserPopperInternal',
  slot: 'userName',
  shouldForwardProp: (propName) => propName !== 'verified',
})<{ verified: boolean }>(({ theme, verified }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(1.5),
  justifyContent: 'center',
  alignItems: 'center',
  margin: theme.spacing(0, 0, 1, 0),
  '& > svg': {
    color: verified ? theme.palette.success.main : theme.palette.warning.main,
  },
}));

const templateId = '11.1.4';

/**
 * Default UserPopperInternal element IDs
 */
export const userPopperInternalElmIds: UserPopperInternalElmIds = {
  root: templateId,
  verifiedIcon: `${templateId}_011`,
  verifiedText: `${templateId}_032`,
  verifyBtn: `${templateId}_033`,
  userName: `${templateId}_034`,
  userMobile: `${templateId}_009`,
  userPhone: `${templateId}_010`,
  userEmail: `${templateId}_024`,
  btnSettings: `${templateId}_020`,
  btnAccesses: `${templateId}_012`,
  btnLogout: `${templateId}_013`,
  accountItem: `${templateId}_023`,
  accountItemRole: `${templateId}_035`,
  accountsDescription: `${templateId}_036`,
  accountsLink: `${templateId}_037`,
};

const UserPopperInternal = forwardRef(
  <TLink extends ElementType = 'button'>(
    {
      id,
      open,
      targetElm,
      onClose,
      user,
      verified = false,
      verifiedText = 'Kontaktní osoba je ověřená',
      unverifiedText = 'Kontaktní osoba není ověřená',
      onVerifiedBtnClick,
      btnVerifiedText = 'Ověřit',
      onLogoutBtnClick,
      btnLogoutText = 'Odhlásit',
      onSettingsBtnClick,
      btnSettingsText = 'Nastavení',
      showSettingsBtn = true,
      onAccessBtnClick,
      btnAccessText = 'Přístupy',
      showAccessBtn = true,
      accountList,
      footerDescription,
      footerLinkText = 'Přejděte na ně kliknutím sem.',
      onFooterLinkClick,
      breakpoint = 'md',
      elmIds = userPopperInternalElmIds,
      componentProps,
      linkComponent,
      ...other
    }: UserPopperInternalProps<TLink>,
    ref: Ref<HTMLDivElement>
  ) => {
    const utilityClasses = useUserPopperInternalUtilityClasses();
    const drawerEnabled = useMediaQuery((theme: Theme) => theme.breakpoints.down(breakpoint));
    const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    const renderSettingsBtn = showSettingsBtn && (onSettingsBtnClick || componentProps?.settingsBtn);
    const renderAccessBtn = showAccessBtn && (onAccessBtnClick || componentProps?.accessBtn);
    const renderFooterLink = (onFooterLinkClick || componentProps?.footerLink) && footerLinkText;
    const renderFooter = footerDescription || renderFooterLink;

    const content = (
      <UserPopperContainer className={clsx(utilityClasses.container)}>
        <VerifiedBox className={clsx(utilityClasses.verifiedBox)} verified={verified}>
          <VerifiedStatus className={clsx(utilityClasses.verifiedStatus)}>
            {verified ? (
              <CheckMarkFill data-elm={elmIds?.verifiedIcon} />
            ) : (
              <QuestionMarkFill data-elm={elmIds?.verifiedIcon} />
            )}
            <Typography textAlign="left" data-elm={elmIds.verifiedText}>
              {verified ? verifiedText : unverifiedText}
            </Typography>
          </VerifiedStatus>
          {!verified && (
            <Button
              data-elm={elmIds?.verifyBtn}
              fullWidth={smDown}
              size="small"
              onClick={onVerifiedBtnClick}
              component={linkComponent as TLink}
              {...(componentProps?.verifiedBtn as ButtonProps<TLink>)}
            >
              {btnVerifiedText}
            </Button>
          )}
        </VerifiedBox>
        <UserPopperContent className={clsx(utilityClasses.content)}>
          <UserName className={clsx(utilityClasses.userName)} verified={verified}>
            <Typography data-elm={elmIds?.userName} variant="h4">
              {user?.name}
            </Typography>
          </UserName>
          <Stack direction="column">
            {user && user.mobile && (
              <Typography data-elm={elmIds?.userMobile} color="gray" component="span">
                {user.mobile}
              </Typography>
            )}
            {user && user.phone && (
              <Typography data-elm={elmIds?.userPhone} color="gray" component="span">
                {user.phone}
              </Typography>
            )}
            {user && user.id && (
              <Typography data-elm={elmIds?.userEmail} color="gray" component="span">
                {user.id}
              </Typography>
            )}
          </Stack>
          {(renderAccessBtn || renderSettingsBtn) && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mt={3} mb={2}>
              {renderSettingsBtn && (
                <Button
                  data-elm={elmIds?.btnSettings}
                  onClick={onSettingsBtnClick}
                  fullWidth
                  size="small"
                  startIcon={<Settings />}
                  component={linkComponent as TLink}
                  {...(componentProps?.settingsBtn as ButtonProps<TLink>)}
                >
                  {btnSettingsText}
                </Button>
              )}
              {renderAccessBtn && (
                <Button
                  data-elm={elmIds?.btnAccesses}
                  onClick={onAccessBtnClick}
                  fullWidth
                  size="small"
                  startIcon={<Key />}
                  component={linkComponent as TLink}
                  {...(componentProps?.accessBtn as ButtonProps<TLink>)}
                >
                  {btnAccessText}
                </Button>
              )}
            </Stack>
          )}
          <Button
            sx={{ mt: 2 }}
            data-elm={elmIds?.btnLogout}
            onClick={onLogoutBtnClick}
            variant="text"
            fullWidth
            size="small"
            startIcon={<Exit />}
            {...componentProps?.logoutBtn}
            {...(componentProps?.logoutBtn as ButtonProps<TLink>)}
          >
            {btnLogoutText}
          </Button>
        </UserPopperContent>
        {((accountList && accountList.length > 0) || renderFooter) && (
          <UserPopperFooter className={clsx(utilityClasses.footer)}>
            {accountList && accountList.length > 0 && (
              <Stack direction="column" divider={<Divider />} gap={1}>
                {accountList.map((account, i) => (
                  <Box data-elm={elmIds?.accountItem} key={i} px={1}>
                    <Stack direction="row" justifyContent="space-between" gap={2}>
                      <Typography variant="caption" textAlign="left">
                        {account.name}
                      </Typography>
                      <Typography
                        minWidth={50}
                        whiteSpace="normal"
                        data-elm={elmIds?.accountItemRole}
                        variant="caption"
                        color="gray"
                        textAlign="right"
                      >
                        {account.role}
                      </Typography>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
            {renderFooter && (
              <Stack direction="column">
                {footerDescription && (
                  <Typography data-elm={elmIds?.accountsDescription} variant="caption" textAlign="center">
                    {footerDescription}
                  </Typography>
                )}
                {renderFooterLink && (
                  <Typography variant="caption" textAlign="center">
                    <Link
                      data-elm={elmIds?.accountsLink}
                      onClick={onFooterLinkClick}
                      component={linkComponent as TLink}
                      {...componentProps?.footerLink}
                    >
                      {footerLinkText}
                    </Link>
                  </Typography>
                )}
              </Stack>
            )}
          </UserPopperFooter>
        )}
      </UserPopperContainer>
    );

    if (drawerEnabled) {
      return (
        <Drawer
          anchor="right"
          open={open}
          onClose={() => {
            onClose && onClose({}, 'backdropClick');
          }}
        >
          {content}
        </Drawer>
      );
    }

    return (
      <Popper
        id={id}
        ref={ref}
        open={open}
        anchorEl={targetElm}
        onClickAway={() => {
          onClose && onClose({}, 'backdropClick');
        }}
        PaperProps={{ mt: '-6px' }}
        className={clsx(utilityClasses.root)}
        {...other}
      >
        {content}
      </Popper>
    );
  }
) as (<TLink extends ElementType = 'button'>(props: UserPopperInternalProps<TLink>) => ReactElement | null) &
  DisplayNameObj;

UserPopperInternal.displayName = 'UserPopperInternal';

export default UserPopperInternal;
